import { parseCookies } from 'nookies'

/**
 * Where a logged-in user is sent when the URL names no page of its own — the bare
 * domain, or Create Account once there is already a session.
 *
 * The decision has two sources that must stay in step: the stored user profile on the
 * client, and the `isShowCaseManagement` cookie server-side, which is the only copy
 * `getInitialProps` can read. Both are funnelled through here so the Cases/Verifications
 * default is written once.
 */

/** Mirrors `customer.modules.caseManagement`; written at login by `storeUserProfile`. */
export const CASE_MANAGEMENT_COOKIE = 'isShowCaseManagement'

export const CASES_ROUTE = '/cases'
export const VERIFICATIONS_ROUTE = '/verifications'

/** Paths a logged-in request hands straight off to the landing route. */
const HANDOFF_PATHS = ['/', '/create-account']

type CaseManagementProfile = {
  customer?: {
    modules?: {
      caseManagement?: boolean
    } | null
  } | null
} | null

type LandingRouteContext = {
  asPath?: string
  req?: { headers?: { cookie?: string } }
} | null

// `parseCookies` is typed against the full Next/Express context unions, none of which a
// caller can build by hand. Only `req.headers.cookie` is ever read, so the narrower shape
// above is what this module accepts and the widening happens once, here.
const cookiesOf = (
  ctx?: LandingRouteContext,
) => parseCookies(ctx as Parameters<typeof parseCookies>[0])

/**
 * Cookies are strings, and the flag is written through nookies, which serialises whatever
 * value it is handed. A customer *without* Case Management therefore stores the literal
 * `"false"` — truthy as a string, which is what sent those accounts to a `/cases` page
 * their module does not serve (sc-9636). Only an exact `"true"` counts as enabled;
 * absent, `"false"` or any stale value reads as off.
 */
export const isCaseManagementCookieEnabled = (
  cookies?: Record<string, string> | null,
): boolean => cookies?.[CASE_MANAGEMENT_COOKIE] === 'true'

/** Landing route from the cookie — the only source available during SSR. */
export const getLandingRoute = (ctx?: LandingRouteContext): string => (
  isCaseManagementCookieEnabled(cookiesOf(ctx)) ? CASES_ROUTE : VERIFICATIONS_ROUTE
)

/** The same decision on the client, from the profile the cookie mirrors. */
export const getLandingRouteForProfile = (profile?: CaseManagementProfile): string => (
  profile?.customer?.modules?.caseManagement ? CASES_ROUTE : VERIFICATIONS_ROUTE
)

/**
 * Landing route for a logged-in request, or `null` when the path has a page of its own
 * and should render normally.
 *
 * `asPath` carries the query string and fragment, so they are stripped before matching:
 * `/?utm_source=email` is still the bare domain, and missing it would render the root as
 * a 404 — there is no `pages/index.tsx` behind it. Matching stays exact after that, so
 * real sub-routes such as `/create-account/:id` keep their own pages.
 */
export const resolveLandingRedirect = (ctx?: LandingRouteContext): string | null => {
  const pathname = ctx?.asPath?.split(/[?#]/)[0]

  return HANDOFF_PATHS.includes(pathname) ? getLandingRoute(ctx) : null
}
